import { RequestBuilder } from '@athena-lib/request-builder';
import type { ProductAndPricesInterface } from '../model/ProductAndPricesInterface';
import type { PriceAndQuantityOptionInterface } from '../model/PriceAndQuantityOptionInterface';
import type { ProductItem } from '../product/model/ProductItem';
import type { FileAppData, StickerWizardProductData } from '../types';

type UploadQuotesResponse = {
  orderId: number;
  ticketId: number;
};

type Extras = {
  material?: string;
  showVat?: boolean;
  quantity?: number;
};
export class StickerWizardService {
  protected qb: RequestBuilder;

  constructor(public baseURL: string = '') {
    this.qb = new RequestBuilder(baseURL);
  }

  public async fetchProductItem(
    productItem: ProductItem,
    lang: string,
    showVat: boolean,
  ): Promise<ProductAndPricesInterface> {
    return (await this.qb
      .createRequest()
      .setURL(`/product/create_item/${productItem.productFamilyName}/${productItem.productName}`)
      .setURLParams({
        attributes: JSON.stringify(productItem.attributes),
        prices: 'true',
        lang: lang,
        show_vat: showVat.toString(),
      })
      .execute()) as ProductAndPricesInterface;
  }

  public async getSheetPreview(productItem: ProductItem, file: FileAppData): Promise<string> {
    return (await this.qb
      .createRequest()
      .setURL(`/quotes/sheet_preview`)
      .setURLParams({
        product_item: JSON.stringify(productItem),
        file: JSON.stringify(file),
      })
      .execute()) as string;
  }

  public async uploadQuotesData(data: unknown): Promise<UploadQuotesResponse> {
    return (await this.qb
      .createRequest()
      .setURL(`/quotes/create_quotes/`)
      .setMethod('POST')
      .setBody(JSON.stringify(data))
      .execute()) as UploadQuotesResponse;
  }

  public async getFormattedPrice(
    price: string,
    lang: string,
  ): Promise<PriceAndQuantityOptionInterface> {
    return (await this.qb
      .createRequest()
      .setURL(`/product/get_formatted_price/`)
      .setURLParams({
        price: price,
        lang: lang,
      })
      .execute()) as PriceAndQuantityOptionInterface;
  }

  public async fetchWizardData(
    productName: string,
    lang: string,
    extras: Extras,
  ): Promise<StickerWizardProductData> {
    const params: Record<string, unknown> = {
      lang: lang,
    };

    if (extras.material) {
      params['material'] = extras.material;
    }

    if (extras.showVat) {
      params['showVat'] = extras.showVat;
    }

    if (extras.quantity) {
      params['quantity'] = extras.quantity;
    }

    const json = await this.getWizardData(productName, params);
    if (!Object.prototype.hasOwnProperty.call(json.productItem.attributes, 'feature')) {
      json.productItem.attributes.feature = [];
    }

    return json;
  }

  private async getWizardData(
    productName: string,
    params: Record<string, unknown>,
  ): Promise<StickerWizardProductData> {
    return (await this.qb
      .createRequest()
      .setURL(`/product/wizard_data/${productName}`)
      .setURLParams(params)
      .execute()) as StickerWizardProductData;
  }
}
